import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import { adminIamApi, type AdminIamAssignmentListItem } from '../api/adminConsole';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { hasAdminPermission } from '../lib/adminPermissions';
import { ADMIN_IAM_ROLES_QUERY_KEY } from '../lib/adminRolesView';
import {
  EXPIRING_MAX_ROWS,
  expiringTabQueries,
  fetchAllAssignmentPages,
  mergeExpiringResults,
  type ExpiringTab,
} from '../lib/adminIamQuery';
import {
  assignmentStatusChip,
  effectiveAssignmentStatus,
  extendedExpiry,
  formatAssignmentExpiry,
  formatIamTimestamp,
} from '../lib/iamAssignmentStatus';
import { roleGrantability } from '../lib/iamGrantability';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';

const TABS: Array<{ value: ExpiringTab; label: string }> = [
  { value: 'next7', label: 'Next 7 days' },
  { value: 'next30', label: 'Next 30 days' },
  { value: 'expired30', label: 'Expired (last 30 days)' },
];

const EXTEND_OPTIONS = [7, 30, 90] as const;

export const EXPIRING_ACCESS_QUERY_KEY = ['adminIamExpiringAccess'] as const;

/**
 * Access that is about to lapse or just did (RBAC Phase 2 item 7). Extending sets a new
 * `expiresAt` (PATCH, audited); "let lapse" just leaves it. Actions need iam.assignments.write.
 */
export function AdminExpiringAccessPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const me = useAdminConsoleMe().data;
  const canAct = hasAdminPermission(me, PermissionKey.IAM_ASSIGNMENTS_WRITE);
  const canReadRoles = hasAdminPermission(me, PermissionKey.IAM_ROLES_READ);
  const [tab, setTab] = useState<ExpiringTab>('next7');
  const [extend, setExtend] = useState<AdminIamAssignmentListItem | null>(null);
  const [days, setDays] = useState<number>(30);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const listQ = useQuery({
    queryKey: [...EXPIRING_ACCESS_QUERY_KEY, tab],
    queryFn: async () => {
      const { queries, keep } = expiringTabQueries(tab);
      const pages = await Promise.all(queries.map((q) => fetchAllAssignmentPages((p) => adminIamApi.assignments(p), q)));
      return {
        items: mergeExpiringResults(tab, pages.map((p) => p.items), keep),
        truncated: pages.some((p) => p.truncated),
      };
    },
    enabled: hasToken,
  });
  const rolesQ = useQuery({
    queryKey: ADMIN_IAM_ROLES_QUERY_KEY,
    queryFn: () => adminIamApi.roles(),
    enabled: hasToken && canAct && canReadRoles,
    retry: false,
  });
  // null = unknown (no iam.roles.read, still loading, or not found): treated as non-delegable.
  const rolePerms = (roleId: string): string[] | null =>
    rolesQ.data?.items.find((r) => r.id === roleId)?.permissions ?? null;

  const save = useMutation({
    mutationFn: (input: { id: string; expiresAt: string }) =>
      adminIamApi.updateAssignment(input.id, { expiresAt: input.expiresAt }),
    onSuccess: async () => {
      setExtend(null);
      await qc.invalidateQueries({ queryKey: EXPIRING_ACCESS_QUERY_KEY });
    },
  });

  const items = useMemo(
    () => (listQ.data?.items ?? []).filter((i) => !dismissed.has(i.id)),
    [listQ.data, dismissed],
  );

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to view expiring access.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Expiring access"
        subtitle="Role assignments that expire soon or expired recently. Expired assignments grant nothing."
      />
      <Tabs value={tab} onChange={(_, v: ExpiringTab) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      <IamErrorAlert error={listQ.error} sx={{ mb: 2 }} />
      {listQ.data?.truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the first {EXPIRING_MAX_ROWS.toLocaleString()} assignments (soonest expiry first) for this window.
        </Alert>
      )}

      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        {listQ.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" sx={adminDataTableSx(theme)}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((a) => {
                const status = effectiveAssignmentStatus(a);
                const chip = assignmentStatusChip(status);
                const grant = me ? roleGrantability(me, { key: a.role.key, permissions: rolePerms(a.role.id) }, a.userId) : null;
                const superAdmin = a.role.key === 'super_admin';
                return (
                  <TableRow key={a.id}>
                    <TableCell>
                      <RouterLink to={`/admin/users/${encodeURIComponent(a.userId)}`}>{a.displayName || a.email}</RouterLink>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {a.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.role.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={formatIamTimestamp(a.expiresAt)}>
                        <span>{formatAssignmentExpiry(a.expiresAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" noWrap title={a.reason ?? ''}>
                        {a.reason || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {canAct && grant && !superAdmin && (
                        <Tooltip title={grant.allowed ? '' : grant.reason}>
                          <span>
                            <Button
                              size="small"
                              disabled={!grant.allowed}
                              onClick={() => {
                                save.reset();
                                setDays(30);
                                setExtend(a);
                              }}
                            >
                              Extend
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canAct && status !== 'expired' && (
                        <Tooltip title="Leave the expiry as it is and hide this row">
                          <Button size="small" color="inherit" onClick={() => setDismissed((s) => new Set(s).add(a.id))}>
                            Let lapse
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      Nothing in this window.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={Boolean(extend)} onClose={() => setExtend(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Extend access</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={save.error} />
            <Typography variant="body2">
              <strong>{extend?.role.name}</strong> for {extend?.email} — currently {formatAssignmentExpiry(extend?.expiresAt)}.
            </Typography>
            <TextField select size="small" label="Extend by" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {EXTEND_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d} days
                </MenuItem>
              ))}
            </TextField>
            {extend && (
              <Typography variant="caption" color="text.secondary">
                New expiry: {formatIamTimestamp(extendedExpiry(extend.expiresAt, days))}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtend(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!extend || save.isPending}
            onClick={() => extend && save.mutate({ id: extend.id, expiresAt: extendedExpiry(extend.expiresAt, days) })}
          >
            Extend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
